/**
 * The prototype's `.modal > .box` dialog, as a component: a fixed header with a Close button, a
 * SCROLLING body, and an optional fixed footer bar.
 *
 * The body scrolls structurally — the box is capped at 90vh and `children` sit in a `.mbody`
 * flex child. Callers used to have to remember `.mb` themselves, and a dialog listing an
 * enquiry's fifty-nine lines simply grew past the viewport, taking its own footer with it.
 *
 * Closes on Escape and on backdrop click, and traps nothing else — these dialogs are
 * short-lived pickers, not full pages.
 */

import { useEffect, type ReactNode } from 'react'
import { Button } from './ui'

export function Modal({
  title,
  onClose,
  children,
  footer,
  width,
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="modal"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="box" role="dialog" aria-modal="true" style={width ? { width } : undefined}>
        <div className="mh">
          <span>{title}</span>
          <Button size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        {/*
          The scrolling middle — see `.mbody`. Structural rather than each caller's job: a dialog
          whose content outgrew the screen used to push its own header and footer out of reach,
          and the file/save button with them.
        */}
        <div className="mbody">{children}</div>
        {footer ? <div className="cfoot">{footer}</div> : null}
      </div>
    </div>
  )
}

/** "Select all" checkbox used in the footer of the supplier pickers. */
export function SelectAllToggle({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label style={{ fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> Select all
    </label>
  )
}
